import React, {useState} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { 
    Box, 
    Button, 
    TextField, 
    Typography, 
    Paper, 
    Grid,
    Container
} from '@mui/material';
import {
    Backdrop, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
} from '@mui/material';
import Modal from '@mui/material/Modal';
import { constants } from '../../util'
import { yupResolver } from '@hookform/resolvers/yup';
// import * as yup from 'yup';
import {useYupSchema} from '../../components/util/hookYupSchema'
import { passwordChange } from '../../services/passwordChange';
import { enqueueSnackbar } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import { showPasswordChange, showWaiting } from '../../store/slices/configSlice';
import { setIsFirstAccess } from '../../store/slices/userSlice';





const PasswordChange = () => {
    const {openPasswordChange, language} = useSelector( state => state.configuration);
    const { isFirstAccess } = useSelector( state => state.userLogged);
    const dispatch = useDispatch();
    const [errorValidations, setErrorValidations] = useState([]);
    
    //TRANSLATION
    const [t, i18n] = useTranslation("global");

    
    const sch = (yup) => yup.object({
        currentPassword: yup.string().required().min(6).max(20),
        newPassword: yup.string().required().min(8).max(20),
        confirmPassword: yup.string().required().min(8).max(20),
    });
    const schema = useYupSchema(sch);


    const { 
        register, 
        handleSubmit, 
        watch, 
        reset,
        formState: { errors } 
    } = useForm({
            resolver: yupResolver(schema),
            defaultValues: {
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }
        });



    const validatePassword = (pwd) => {
        const rules = [
            { test: pwd.length >= 8 && pwd.length <= 15, message: language==='es' ? 'La contraseña debe tener entre 8 y 15 caracteres' : 'Password must have between 8 and 15 characters' },
            { test: /[A-Z]/.test(pwd), message: language==='es' ? 'Debe incluir al menos 1 mayúscula' : 'Must include at least 1 capital letter' },
            { test: /[a-z]/.test(pwd), message: language==='es' ? 'Debe incluir al menos 1 minúscula' : 'Must include at least 1 lowercase letter' },
            { test: /\d/.test(pwd), message: language==='es' ? 'Debe incluir al menos 1 número' : 'Must include at least 1 number' },
            { test: /[*.,\-_/()%$#]/.test(pwd), message: language==='es' ? 'Debe incluir al menos 1 símbolo (*.,-_/()%$#)' : 'Must include at least 1 symbol (*.,-_/()%$#)' },
        ];

        return rules.filter(rule => !rule.test);
    };


    const handleClose = () => {
        reset();
        if (!isFirstAccess)
            dispatch(showPasswordChange(false));
    }

    const onSubmit = (data) => {
        dispatch(showWaiting(true));

        if (data.newPassword === data.confirmPassword){
            const arrValidations = validatePassword(data.newPassword);
            if (arrValidations.length > 0){
                setErrorValidations(arrValidations);
                dispatch(showWaiting(false));
            }
            else{
                setErrorValidations([]);
                passwordChange(data.currentPassword, newPassword)
                    .then((rsp)=>{
                        setTimeout(() => {
                            enqueueSnackbar(rsp.message, { variant: rsp.code==='00' ? 'success' : 'error', persist: false });
                            dispatch(showWaiting(false));
                            dispatch(setIsFirstAccess(false));
                            if (rsp.code==='00')
                                handleClose();
                        }, 1000);
                    })
                    .catch(error=>dispatch(showWaiting(false)));
            }
        }
        else{
            setTimeout(() => {
                dispatch(showWaiting(false));
                enqueueSnackbar(
                    language==='es'
                        ? 'Las contraseñas no coinciden'
                        : 'Passwords are not equal'
                , { variant: 'error', persist: false });
            }, 500);
        }
    };

    // Para comparar las nuevas contraseñas
    const newPassword = watch('newPassword');

    return(
        <Modal
                open={openPasswordChange}
                onClose={handleClose}
            >
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate
                    sx={{ ...constants.styles.modalBox,
                    width: {
                        sxs: '95%', 
                        xs: '95%',  // Celular-vertical
                        sm: '80%',  // Ipad Vertical
                        md: '40%',  // Ipad Horizontal
                        lg: '40%',  // Laptop-PC
                        xl: '40%',  // Laptop-PC,
                    },
                    maxwidth: {
                        sxs: '400px', 
                        xs: '400px',  // Celular-vertical
                        sm: '50vw',  // Ipad Vertical
                        md: '700px',  // Ipad Horizontal
                        lg: '700px',  // Laptop-PC
                        xl: '700px',  // Laptop-PC,
                    },
                    padding: '20px'
                        
                }}
                >
                <Grid container spacing={3} direction="column" sx={{height: '100%'}}>
                    <Grid item>
                        <div className='text-regular2'>
                            {t("passwordChange.title")}
                        </div>
                        <hr />
                    </Grid>
                    <Grid item>
                        <TextField
                            fullWidth
                            label={t("passwordChange.oldPassword")}
                            type="password"
                            autoComplete="current-password"
                            {...register('currentPassword', { 
                            required: 'Este campo es requerido',
                            minLength: {
                                value: 6,
                                message: 'La contraseña debe tener al menos 6 caracteres'
                            }
                            })}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword?.message}
                        />
                    </Grid>
                    
                    <Grid item>
                        <TextField
                            fullWidth
                            label={t("passwordChange.newPassword")}
                            type="password"
                            autoComplete="new-password"
                            {...register('newPassword', { 
                            required: 'Este campo es requerido',
                            minLength: {
                                value: 8,
                                message: 'La contraseña debe tener al menos 8 caracteres'
                            },
                            pattern: {
                                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                                message: 'Debe contener al menos una mayúscula, una minúscula y un número'
                            }
                            })}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                        />
                    </Grid>
                    
                    <Grid item>
                        <TextField
                            fullWidth
                            label={t("passwordChange.passwordConfirm")}
                            type="password"
                            autoComplete="new-password"
                            {...register('confirmPassword', { 
                            required: 'Por favor confirma tu nueva contraseña',
                            validate: value => 
                                value === newPassword || 'Las contraseñas no coinciden'
                            })}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                        />
                        {
                            errorValidations.map(error => (
                                <Typography key={error.message} sx={{color:'red', fontSize:'12px'}}>{error.message}</Typography>
                            ))
                        }
                    </Grid>
                    <Grid item sx={{display:'flex', justifyContent:'flex-end', marginTop:'10px'}}>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ ...constants.styles.mainButton, width: '30%' }}
                        >
                            {t("passwordChange.okButton")}
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={()=>handleClose()}
                            sx={{ ...constants.styles.secondaryButton, width: '30%', marginLeft:'10px' }}
                        >
                            {t("passwordChange.cancelButton")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
}

export default PasswordChange;